import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { db } from '../../firebase';
import { doc, getDoc } from 'firebase/firestore';
import ReactMarkdown from 'react-markdown';
import Papa from 'papaparse';
import { Tabs, Card, Collapse, Table, Button } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';

const FIGURE_MAP = {
  FOMC: 'UnitedStates.png',
  PBoC: 'China.png',
  BoJ: 'Japan.png',
  BoE: 'UnitedKingdom.png',
  SNB: 'Switzerland.png',
  BCB: 'Brazil.png',
  RBI: 'India.png',
  ECB: 'EuropeanUnion.png',
  CBR: 'Russia.png',
  CBCT: 'Taiwan.png',
  MAS: 'Singapore.png',
  BoK: 'SouthKorea.png',
  RBA: 'Australia.png',
  BoI: 'Israel.png',
  BoC: 'Canada.png',
  BdeM: 'Mexico.png',
  NBP: 'Poland.png',
  CBRT: 'Turkey.png',
  BoT: 'Thailand.png',
  CBE: 'Egypt.png',
  BNM: 'Malaysia.png',
  BSP: 'Philippines.png',
  CBoC: 'Chile.png',
  BCRP: 'Peru.png',
  BanRep: 'Colombia.png'
};

const REGION_MAP = {
  FOMC: 'North America', BoC: 'North America', BdeM: 'Latin America', BCB: 'Latin America',
  CBoC: 'Latin America', BCRP: 'Latin America', BanRep: 'Latin America',
  BoE: 'Europe', ECB: 'Europe', SNB: 'Europe', NBP: 'Europe', CBR: 'Europe',
  CBRT: 'Europe / Asia', BoI: 'Middle East', CBE: 'Africa',
  PBoC: 'Asia', BoJ: 'Asia', RBI: 'Asia', CBCT: 'Asia', BoK: 'Asia',
  MAS: 'ASEAN', BNM: 'ASEAN', BSP: 'ASEAN', BoT: 'ASEAN', RBA: 'Oceania'
};

const BANKS = [
  { abbr: 'FOMC', name: 'Federal Open Market Committee', href: '/fomc' },
  { abbr: 'PBoC', name: 'People’s Bank of China', href: '/pboc' },
  { abbr: 'BoJ', name: 'Bank of Japan', href: '/boj' },
  { abbr: 'BoE', name: 'Bank of England', href: '/boe' },
  { abbr: 'SNB', name: 'Swiss National Bank', href: '/snb' },
  { abbr: 'BCB', name: 'Central Bank of Brazil', href: '/bcb' },
  { abbr: 'RBI', name: 'Reserve Bank of India', href: '/rbi' },
  { abbr: 'ECB', name: 'European Central Bank', href: '/ecb' },
  { abbr: 'CBR', name: 'Central Bank of Russia', href: '/cbr' },
  { abbr: 'CBCT', name: 'Central Bank of Taiwan', href: '/cbct' },
  { abbr: 'MAS', name: 'Monetary Authority of Singapore', href: '/mas' },
  { abbr: 'BoK', name: 'Bank of Korea', href: '/bok' },
  { abbr: 'RBA', name: 'Reserve Bank of Australia', href: '/rba' },
  { abbr: 'BoI', name: 'Bank of Israel', href: '/boi' },
  { abbr: 'BoC', name: 'Bank of Canada', href: '/boc' },
  { abbr: 'BdeM', name: 'Bank of Mexico', href: '/bdem' },
  { abbr: 'NBP', name: 'National Bank of Poland', href: '/nbp' },
  { abbr: 'CBRT', name: 'Central Bank of Turkey', href: '/cbrt' },
  { abbr: 'BoT', name: 'Bank of Thailand', href: '/bot' },
  { abbr: 'CBE', name: 'Central Bank of Egypt', href: '/cbe' },
  { abbr: 'BNM', name: 'Bank Negara Malaysia', href: '/bnm' },
  { abbr: 'BSP', name: 'Bangko Sentral ng Pilipinas', href: '/bsp' },
  { abbr: 'CBoC', name: 'Central Bank of Chile', href: '/cboc' },
  { abbr: 'BCRP', name: 'Central Reserve Bank of Peru', href: '/bcrp' },
  { abbr: 'BanRep', name: 'Banco de la República (Colombia)', href: '/banrep' }
];

const BankPage = () => {
  const { bankname } = useParams();
  const [data, setData] = useState(null);
  const [stats, setStats] = useState(null);
  const [links, setLinks] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [navActive, setNavActive] = useState(false);

  const selectedBank = BANKS.find(b => b.href.slice(1) === bankname);
  const region = selectedBank ? REGION_MAP[selectedBank.abbr] : '';
  const flag = selectedBank ? `/static/images/flags/${selectedBank.abbr}.png` : '';

  const guideHref = selectedBank ? `/static/annotation_guides/${selectedBank.abbr}.pdf` : null;

// (optional) check if the file exists to show a friendly message
const [guideAvailable, setGuideAvailable] = useState(null); // null | true | false
useEffect(() => {
  let alive = true;
  if (!guideHref) { setGuideAvailable(false); return; }
  setGuideAvailable(null);
  fetch(guideHref, { method: 'HEAD' })
    .then(r => { if (alive) setGuideAvailable(r.ok); })
    .catch(() => { if (alive) setGuideAvailable(false); });
  return () => { alive = false; };
}, [guideHref]);

  // Prepare markdown without first heading line
  const mandateMarkdown = data?.mandate?.split('\n').slice(1).join('\n') || '';
  const structureMarkdown = data?.structure?.split('\n').slice(1).join('\n') || '';

  useEffect(() => {
    if (!bankname) return;
    (async () => {
      const ref = doc(db, 'countries', bankname);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        const d = snap.data();
        setData(d);
        setStats(d.statistics);
        setLinks([
          { label: `${bankname.toUpperCase()} Official Site`, url: d.official_website },
          { label: `${bankname.toUpperCase()} Dataset`, url: d.annotated_dataset_link },
          { label: `${bankname.toUpperCase()} Stance Model`, url: d.stance_model_link },
          { label: `${bankname.toUpperCase()} Time Model`, url: d.time_model_link },
          { label: `${bankname.toUpperCase()} Certainty Model`, url: d.certain_model_link }
        ]);
      }
    })();
  }, [bankname]);

  useEffect(() => {
    if (!selectedBank) return;
  
    fetch(`https://raw.githubusercontent.com/rutwikroutu/hawkishness-csvs/refs/heads/main/${selectedBank.abbr}.csv`)
      .then(r => r.text())
      .then(txt => {
        const { data: rows } = Papa.parse(txt, { header: true, skipEmptyLines: true });
  
        const mapped = rows.map((r, i) => {
          const rawVal = parseFloat(r['Hawkishness']);
          const d = new Date(r['Hawkishness Date']);
          const ms = d.getTime();
          const ts = Number.isNaN(ms) ? null : ms;
  
          return {
            key: i,
            date: r['Hawkishness Date'],
            ts,
            // round to 3 decimals for consistency in chart + table
            hawkishness: Number.isFinite(rawVal) ? Number(rawVal.toFixed(3)) : null
          };
        });
  
        // keep only valid dates and sort past → present
        const sorted = mapped.filter(x => x.ts !== null).sort((a, b) => a.ts - b.ts);
  
        // use the same sorted data for table and chart so both are sequential
        setTableData(sorted);
        setChartData(sorted);
      })
      .catch(console.error);
  }, [selectedBank]);
  if (!data || !selectedBank) return <p className="has-text-centered mt-10 is-size-4">Loading...</p>;

  const columns = [
    { title: 'Date', dataIndex: 'date', key: 'date' },
    { title: 'Hawkishness', dataIndex: 'hawkishness', key: 'hawkishness', render: v => v==null? '-' : v.toFixed(3) }
  ];

  return (
    <div style={{paddingBottom: 7}}>
      <nav className="navbar is-spaced" role="navigation" aria-label="main navigation">
        <div className="navbar-brand">
          <Link to="/" className="navbar-item">
            <img src="/static/images/logo2.png" alt="Logo" />
          </Link>
          <button
            className={`navbar-burger ${navActive ? 'is-active' : ''}`}
            aria-expanded={navActive}
            onClick={() => setNavActive(!navActive)}
          >
            <span aria-hidden />
            <span aria-hidden />
            <span aria-hidden />
          </button>
        </div>
        <div className={`navbar-menu ${navActive ? 'is-active' : ''}`}>
          <div className="navbar-start" style={{ display: 'flex', flexGrow: 1, justifyContent: 'center' }}>
            <div className="navbar-item has-dropdown is-hoverable">
              <a className="navbar-link">Select Central Bank</a>
              <div className="navbar-dropdown multi-column">
                {BANKS.map(b => (
                  <Link key={b.abbr} to={b.href} className="navbar-item">
                    <img
                      src={`/static/images/flags/${b.abbr}.png`}
                      alt={b.abbr}
                      style={{ marginRight: 8, height: 16, border: '1px solid #000', padding: 1 }}
                    />
                    {b.abbr}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Header */}
      <section className="section py-10">
  {/* Header */}
  <div
    className="container is-max-desktop has-text-centered"
    style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 16, flexDirection: "column" }}
  >
    <img
      src={flag}
      alt={selectedBank.abbr}
      loading="lazy"
      decoding="async"
      className="rounded-[3px] ring-1 ring-black/20"
      style={{ height: 170 }}
    />
    <div className="inline-flex items-center gap-3">
      <h1 className="m-0 text-3xl md:text-4xl font-semibold tracking-tight text-gray-900">
        {selectedBank.name}
      </h1>

      {/* keep your Bulma tag, just a touch of spacing */}
      <span className="tag is-info is-medium md:is-large">{region}</span>
    </div>
  </div>

  {/* Tabs */}
  <div className="container is-max-desktop mt-8 md:mt-10">
    <Tabs defaultActiveKey="1" type="line" tabBarGutter={32}>
      {/* ================= OVERVIEW ================= */}
      <Tabs.TabPane tab="Overview" key="1">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-6">
          <Card
            title={<span className="text-gray-800">Mandate</span>}
            headStyle={{ borderRadius: 10, borderBottom: "1px solid rgba(0,0,0,0.06)" }}
            bodyStyle={{ paddingTop: 14, paddingBottom: 16 }}
            className="rounded-xl"
            style={{ minWidth: 280 }}
          >
            <ReactMarkdown
              components={{
                p: (props) => <p className="text-[15px] md:text-[16px] leading-7 md:leading-8 text-gray-700 my-3" {...props} />,
                strong: (props) => <strong className="text-gray-900 font-semibold" {...props} />,
                li: (props) => <li className="text-[15px] md:text-[16px] leading-7 md:leading-8 text-gray-700 my-1.5 ml-5 list-disc" {...props} />,
                h3: (props) => <h3 className="text-base font-semibold text-gray-800 mt-4 mb-2" {...props} />,
              }}
            >
              {mandateMarkdown}
            </ReactMarkdown>
          </Card>

          <Card
            title={<span className="text-gray-800">Structure</span>}
            headStyle={{ borderRadius: 10, borderBottom: "1px solid rgba(0,0,0,0.06)" }}
            bodyStyle={{ paddingTop: 14, paddingBottom: 16 }}
            className="rounded-xl"
            style={{ minWidth: 280 }}
          >
            <ReactMarkdown
              components={{
                p: (props) => <p className="text-[15px] md:text-[16px] leading-7 md:leading-8 text-gray-700 my-3" {...props} />,
                strong: (props) => <strong className="text-gray-900 font-semibold" {...props} />,
                li: (props) => <li className="text-[15px] md:text-[16px] leading-7 md:leading-8 text-gray-700 my-1.5 ml-5 list-disc" {...props} />,
                h3: (props) => <h3 className="text-base font-semibold text-gray-800 mt-4 mb-2" {...props} />,
              }}
            >
              {structureMarkdown}
            </ReactMarkdown>
          </Card>
        </div>
      </Tabs.TabPane>

      {/* ================= ECONOMIC ANALYSIS ================= */}
      <Tabs.TabPane tab="Economic Analysis" key="2">
        <div className="flex justify-end mb-3">
          <Button
            icon={<DownloadOutlined />}
            size="small"
            href={`https://raw.githubusercontent.com/rutwikroutu/hawkishness-csvs/refs/heads/main/${selectedBank.abbr}.csv`}
            download
          >
            Download CSV
          </Button>
        </div>

        <h3 className="subtitle is-5 mb-4 text-gray-800">CPI vs Hawkishness Correlation</h3>

        {FIGURE_MAP[selectedBank.abbr] && (
          <div className="text-center mb-6">
            <img
              src={`/static/econ_figures/${FIGURE_MAP[selectedBank.abbr]}`}
              alt="Correlation"
              loading="lazy"
              decoding="async"
              className="mx-auto rounded-lg shadow-sm ring-1 ring-black/10"
              style={{  width: "100%", height: "auto" }}
            />
          </div>
        )}

        <Collapse ghost className="mt-4">
          <Collapse.Panel header="Show/Hide Hawkishness Data" key="1">
            <Table
              columns={columns}
              dataSource={tableData}
              size="small"
              pagination={{ pageSize: 10 }}
              scroll={{ x: "max-content" }}
            />
          </Collapse.Panel>
        </Collapse>
      </Tabs.TabPane>
    </Tabs>
  </div>
</section>

<section
  className="section py-10 px-6"
  style={{ backgroundColor: "#e7e7e7", margin: 30, borderRadius: 20 }}
>
  <div className="container is-max-desktop">
    <div className="has-text-centered mb-6">
      <p className="heading is-size-5 mb-1 tracking-wide text-gray-700">
        Annotation Guide — {selectedBank.abbr}
      </p>
      <div className="mx-auto h-[2px] w-24 rounded-full bg-gray-400/60" />
    </div>

    <div
      className="
        rounded-2xl bg-white/25 backdrop-blur-sm ring-1 ring-white/50
        shadow-sm p-4 md:p-5
        transition-all duration-300 ease-out
        hover:shadow-md hover:ring-white/70
      "
    >
      {guideAvailable === null && (
        <div className="py-10 text-center text-gray-600">Checking for guide…</div>
      )}

      {guideAvailable === false && (
        <div className="py-10 text-center">
          <p className="text-gray-700 mb-2">
            No annotation guide found for {selectedBank.abbr}.
          </p>
          <p className="text-gray-500 text-sm">
            Place the PDF at <code>/static/annotation_guides/{selectedBank.abbr}.pdf</code>.
          </p>
        </div>
      )}

      {guideAvailable && (
        <div className="space-y-4">
          {/* Toolbar */}
          <div className="flex items-center justify-end gap-2">
            <a
              href={guideHref}
              target="_blank"
              rel="noreferrer"
              className="button is-light is-rounded !bg-white !text-gray-800 hover:!bg-gray-100 shadow-sm transition px-3 py-2"
              aria-label="Open annotation guide in a new tab"
            >
              Open
            </a>
            <a
              href={guideHref}
              download
              className="button is-rounded !bg-gray-700 hover:!bg-gray-800 !border-none !text-white shadow-sm transition px-3 py-2"
              aria-label="Download annotation guide PDF"
            >
              Download
            </a>
          </div>

          {/* Embedded PDF */}
          <div className="rounded-xl overflow-hidden ring-1 ring-black/10 bg-white">
            <object
              data={guideHref}
              type="application/pdf"
              className="w-full"
              style={{ height: "70vh" }}
            >
              <iframe
                title={`${selectedBank.abbr} annotation guide`}
                src={guideHref}
                className="w-full"
                style={{ height: "70vh", border: 0 }}
              />
            </object>
          </div>
        </div>
      )}
    </div>
  </div>
</section>




      <section
  className="section py-12 px-6"
  style={{
    backgroundColor: "#e7e7e7",      // slightly lighter than #e0e0e0
    margin: 30,
    borderRadius: 20,
  }}
>
  <div className="container">
    {/* Title */}
    <div className="has-text-centered mb-8">
      <p className="heading is-size-5 mb-1 tracking-wide text-gray-700">
        Statistics for {bankname.toUpperCase()} over {stats?.years ?? "-"} years of data
      </p>
      <div className="mx-auto h-[2px] w-24 rounded-full bg-gray-400/60" />
    </div>

    {/* Primary stats (centered) */}
    <div className="max-w-5xl mx-auto">
      <div className="columns is-multiline is-variable is-2 is-centered">
        {[
          { label: "Years",      value: stats?.years ?? "-" },
          { label: "Sentences",  value: stats?.sentences ?? "-" },
          {
            label: "Chars/Sent",
            value: stats?.sentences ? (stats.characters / stats.sentences).toFixed(2) : "-",
          },
          {
            label: "Words/Sent",
            value: stats?.sentences ? (stats.words / stats.sentences).toFixed(2) : "-",
          },
        ].map((stat) => (
          <div key={stat.label} className="column is-one-quarter has-text-centered">
            <div
              className="
                rounded-2xl bg-white/20 backdrop-blur-sm ring-1 ring-white/40
                px-6 py-6 flex flex-col items-center
                transition-all duration-300 ease-out
                hover:bg-white/35 hover:shadow-lg hover:ring-white/60
                motion-safe:hover:-translate-y-[1px]
              "
            >
              <p className="uppercase tracking-widest text-xs text-gray-700 mb-2">{stat.label}</p>
              <p className="text-3xl font-semibold text-gray-900">{stat.value}</p>
            </div>
          </div>
        ))}
      </div>
    </div>

    {/* Comparison */}
    <div className="has-text-centered mt-10 mb-4">
      <p className="subtitle is-size-6 text-gray-700/80">
        For comparison, average per bank (24 others)
      </p>
      <div className="mx-auto h-[1px] w-16 rounded-full bg-gray-400/50" />
    </div>

    {/* Comparison stats (same hover + centered) */}
    <div className="max-w-5xl mx-auto">
      <div className="columns is-multiline is-variable is-2 is-centered">
        {[
          { label: "Years",      value: 17.64 },
          { label: "Sentences",  value: Math.floor(374053 / 25) },
          { label: "Chars/Sent", value: 166.54 },
          { label: "Words/Sent", value: 26.68 },
        ].map((stat) => (
          <div key={stat.label} className="column is-one-quarter has-text-centered">
            <div
              className="
                rounded-2xl bg-white/30 ring-1 ring-white/60
                px-6 py-6 flex flex-col items-center
                transition-all duration-300 ease-out
                hover:bg-white/45 hover:shadow-lg hover:ring-white
                motion-safe:hover:-translate-y-[1px]
              "
            >
              <p className="uppercase tracking-widest text-[11px] text-gray-700 mb-2">{stat.label}</p>
              <p className="text-2xl font-semibold text-gray-800">{stat.value}</p>
            </div>
          </div>
        ))}
      </div>
    </div>

    {/* CTAs */}
    <div className="has-text-centered mt-10">
      <p className="heading is-size-6 mb-3 text-gray-700/90 tracking-wide">
        Important Resources
      </p>

      {/* Primary bank-specific links — neutral pills */}
      <div className="buttons is-centered is-flex-wrap-wrap gap-2">
        {links.map((link, i) =>
          link.url ? (
            <a
              key={i}
              href={link.url}
              target="_blank"
              rel="noreferrer"
              className="
                button is-rounded m-1
                !bg-gray-700 hover:!bg-gray-800 !border-none !text-white
                shadow-sm hover:shadow-md transition-all duration-200 ease-out
                px-4
              "
            >
              {link.label}
            </a>
          ) : null
        )}
      </div>

      {/* Global annotated links — light pills */}
      <div className="buttons is-centered is-flex-wrap-wrap mt-4 gap-2">
        <a
          href="https://huggingface.co/datasets/gtfintechlab/all_annotated_sentences_25000"
          target="_blank"
          rel="noreferrer"
          className="
            button is-light is-rounded m-1
            !bg-white !text-gray-800 hover:!bg-gray-100
            shadow-sm hover:shadow-md transition-all duration-200 ease-out
            px-4
          "
        >
          Entire Annotated Dataset
        </a>
        <a
          href="https://huggingface.co/gtfintechlab/model_WCB_stance_label"
          target="_blank"
          rel="noreferrer"
          className="
            button is-light is-rounded m-1
            !bg-white !text-gray-800 hover:!bg-gray-100
            shadow-sm hover:shadow-md transition-all duration-200 ease-out
            px-4
          "
        >
          All Stance Label Model
        </a>
        <a
          href="https://huggingface.co/gtfintechlab/model_WCB_time_label"
          target="_blank"
          rel="noreferrer"
          className="
            button is-light is-rounded m-1
            !bg-white !text-gray-800 hover:!bg-gray-100
            shadow-sm hover:shadow-md transition-all duration-200 ease-out
            px-4
          "
        >
          All Time Label Model
        </a>
        <a
          href="https://huggingface.co/gtfintechlab/model_WCB_certain_label"
          target="_blank"
          rel="noreferrer"
          className="
            button is-light is-rounded m-1
            !bg-white !text-gray-800 hover:!bg-gray-100
            shadow-sm hover:shadow-md transition-all duration-200 ease-out
            px-4
          "
        >
          All Certain Label Model
        </a>
      </div>
    </div>
  </div>
</section>


    </div>
  );
};

export default BankPage;
